import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserSource } from 'src/types';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { filter, first } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

export type RegisterParams = {
  email: string;
  is_terms_accepted: boolean;
  is_privacy_accepted: boolean;
};

export type RegisterResponse = {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  token_type: string;
};

export type VerifyEmailParams = {
  name: string;
  password: string;
  is_newsletter_enabled: boolean;
  token: string;
};

export type SendPasswordResetEmailParams = {
  email: string;
};

export type ResetPasswordParams = {
  token: string;
  email: string;
  password: string;
  password_confirmation: string;
};

export type ForceResetPasswordParams = {
  old_password: string;
  password: string;
  password_confirmation: string;
};

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private refreshingTokens = new BehaviorSubject<boolean>(false);
  private requestsWaiting = new BehaviorSubject<number>(0);

  private get idTokenHint(): string | null { return localStorage.getItem('id_token_hint'); }
  private set idTokenHint(idToken: string | null) {
    if (idToken) {
      localStorage.setItem('id_token_hint', idToken);
    } else {
      localStorage.removeItem('id_token_hint');
    }
  }
  private get accessToken(): string | null { return localStorage.getItem('access_token'); }
  private set accessToken(accessToken: string | null) {
    if (accessToken) {
      localStorage.setItem('access_token', accessToken);
    } else {
      localStorage.removeItem('access_token');
    }
  }
  private get refreshToken(): string | null { return localStorage.getItem('refresh_token'); }
  private set refreshToken(refreshToken: string | null) {
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
    } else {
      localStorage.removeItem('refresh_token');
    }
  }
  private get expiresAt(): string | null { return localStorage.getItem('expires_at'); }
  private set expiresAt(expiresAt: string | null) {
    if (expiresAt) {
      localStorage.setItem('expires_at', expiresAt);
    } else {
      localStorage.removeItem('expires_at');
    }
  }

  private _origin: UserSource;
  get origin(): UserSource { return this._origin; }

  authSubject = new BehaviorSubject<boolean>(!!this.accessToken);
  get isAuthenticated(): boolean { return this.authSubject.value; };

  private logoutTimeout?;

  constructor(
    private router: Router,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
  ) {
    this.setDefaultUserOrigin();
    this.setRequestInterceptor();
    this.setResponseInterceptor();
  }

  setTokens(data: {
    access_token: string,
    refresh_token: string,
    expires_in: number,
    id_token: string,
  }): void {
    this.accessToken = data.access_token;
    this.refreshToken = data.refresh_token;
    this.idTokenHint = data.id_token;
    this.expiresAt = (new Date().valueOf() + data.expires_in * 1000).toString();
    this.authSubject.next(true);
  };

  clearTokens(): void {
    this.refreshToken = null;
    this.accessToken = null;
    this.expiresAt = null;
    this.idTokenHint = null;
  }

  async login(email: string, password: string): Promise<void> {
    try {
      this.clearTokens();

      const url = environment.baseUrl + `/api/auth/login`;
      const result = await axios.post(url, {
        email,
        password,
      });

      const {
        access_token,
        refresh_token,
        expires_in,
      } = result.data;

      this.accessToken = access_token;
      this.refreshToken = refresh_token;
      this.expiresAt = (new Date().valueOf() + expires_in * 1000).toString();
      this.authSubject.next(true);

      await this.router.navigateByUrl(environment.afterLoginPath);
    } catch (error) {
      throw error;
    }
  }

  async loginWithCustomToken(customToken: string, origin: UserSource = 'ORGANIC'): Promise<void> {
    try {
      throw new Error('Unimplemented');
      this.setOrigin(origin);

      // this.router.navigate(['user/cases']);
    } catch (error) {
      throw error;
    }
  }

  async createPayeeAccountFromCashbook(ssoToken: string): Promise<string> {
    throw new Error('Unimplemented');
  }

  async register(params: RegisterParams): Promise<void> {
    const url = environment.baseUrl + `/api/auth/register`;
    const response = await axios.post<RegisterResponse>(url, params);
    const {
      access_token,
      expires_in,
      refresh_token,
    } = response.data;

    this.accessToken = access_token;
    this.refreshToken = refresh_token;
    this.expiresAt = (new Date().valueOf() + expires_in * 1000).toString();
    this.authSubject.next(true);

    await this.router.navigateByUrl('verify-email');
  }

  async registerPassword(password: string): Promise<void> {
    throw new Error('Unimplemented');
  }

  async sendPasswordResetEmail(params: SendPasswordResetEmailParams): Promise<void> {
    const url = environment.baseUrl + `/api/auth/send-reset-password-email`;
    await axios.post(url, params);
  }

  async resendVerifyEmail(): Promise<void> {
    const url = environment.baseUrl + `/api/auth/send-email-verification`;
    await axios.post(url);
  }

  async logout(): Promise<void> {
    if (this.logoutTimeout) {
      clearTimeout(this.logoutTimeout);
    }

    if (!this.isAuthenticated) {
      this.clearTokens();
      await this.router.navigateByUrl('login');
      return;
    }

    this.refreshToken = null;

    if (this.idTokenHint) {
      const url = environment.baseUrl + `/api/oauth/logout`;
      await axios.post(url, {
        id_token_hint: this.idTokenHint,
      }).catch(e => console.warn('Error while logging out', e));
    }

    this.authSubject.next(false);
    this.clearTokens();

    await this.router.navigateByUrl('login');
  }

  async resetPassword(params: ResetPasswordParams, redirectTo = 'forgot-password-new-password-success'): Promise<void> {
    const url = environment.baseUrl + `/api/auth/reset-password`;
    await axios.post(url, params);

    await this.router.navigateByUrl(redirectTo);
  }

  async forceResetPassword(params: ForceResetPasswordParams): Promise<void> {
    const url = environment.baseUrl + `/api/auth/force-reset-password`;
    await axios.post(url, params);
  }

  async quickHardColLogin(email?: string): Promise<void> {
    throw new Error('Unimplemented');
  }

  async finalizeSignup(params: VerifyEmailParams, redirectTo = 'user/add-new-client-onboarding'): Promise<void> {
    const url = environment.baseUrl + `/api/auth/verify-email`;
    await axios.post(url, params);

    await this.router.navigateByUrl(redirectTo);
  }

  async enable2FA(): Promise<void> {
    const url = environment.baseUrl + `/api/auth/2fa/register`;

    await axios.post(url);
    localStorage.setItem('next', this.router.url);
    this.router.navigateByUrl('2fa/verify');
  }

  async submitMFACode(code: string): Promise<void> {
    const url = new URL(environment.baseUrl + `/api/auth/2fa/token`);
    url.searchParams.append('one_time_password', code);

    await axios.post(url.toString());
    this.authSubject.next(true);
    const next = localStorage.getItem('next') || '/user';
    this.router.navigateByUrl(next);
  }

  async resendMFACode(): Promise<void> {
    await axios.post(environment.baseUrl + `/api/auth/2fa/resend`);
  };

  getOriginOfPartnerId(partnerId: string): UserSource | null {
    switch (partnerId) {
      case 'szamlazzhu': return 'PLUGIN_SZAMLAZZHU';
      default: return null;
    }
  }

  private setOrigin(origin: UserSource | null): void {
    if (origin) {
      this._origin = origin;
    }
    // TODO
    // if (origin === null) {
    //   sessionStorage.removeItem('user-origin');
    //   localStorage.removeItem('user-origin');
    // } else {
    //   switch (persistance) {
    //     case firebase.auth.Auth.Persistence.LOCAL:
    //       localStorage.setItem('user-origin', origin);
    //       break;
    //     case firebase.auth.Auth.Persistence.SESSION:
    //       sessionStorage.setItem('user-origin', origin);
    //       break;
    //     case firebase.auth.Auth.Persistence.NONE: break;
    //     default: throw new Error('Persistance parameter is not set');
    //   }
    // }
  }

  private setLogoutTimeout(): void {
    // Logout after 15 minutes
    this.logoutTimeout = setTimeout(() => this.logout(), 900_000);
  }

  private setDefaultUserOrigin(): void {
    let userOrigin = sessionStorage.getItem('user-origin') as UserSource;
    if (userOrigin) {
      this.setOrigin(userOrigin);
    } else {
      userOrigin = localStorage.getItem('user-origin') as UserSource;
      if (userOrigin) {
        this.setOrigin(userOrigin);
      }
    }
  }

  private setRequestInterceptor(client: AxiosInstance = axios): void {
    client.interceptors.request.use(
      async config => {
        if (this.logoutTimeout) {
          clearTimeout(this.logoutTimeout);
        }
        this.setLogoutTimeout();

        if (this.refreshingTokens.value && !config.data?.refresh_token) {
          this.requestsWaiting.next(this.requestsWaiting.value + 1);
          await this.refreshingTokens.pipe(filter(v => !v), first()).toPromise();
          this.requestsWaiting.next(this.requestsWaiting.value - 1);
        }

        const access_token = localStorage.getItem('access_token');
        const user_email = localStorage.getItem('user_email'); // for testing purposes

        if (access_token && !user_email) {
          config.headers = {
            'Authorization': `Bearer ${this.accessToken}`,
            'Accept': 'application/json',
          };
        } else if (access_token && user_email) {
          config.headers = {
            'Authorization': `Bearer ${this.accessToken}`,
            'Accept': 'application/json',
            'User-Email': user_email
          };
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      });
  }

  private setResponseInterceptor(
    client: AxiosInstance = axios,
    unauthorizedPolicy: 'retry' | 'login' = 'retry',
  ): void {
    client.interceptors.response.use(
      async res => {
        // if (!this.refreshingTokens.value) {
        //   this.retrying = false;
        //   await this.refreshAccessToken();
        // }

        return res;
      },
      async (error: AxiosError) => {
        try {
          if (error.response?.status === 401) {
            return await this.handleUnauthorized(error, unauthorizedPolicy);
          } else if (error.response?.status === 403) {
            return await this.handleForbidden(error);
          }

          return Promise.reject(error);
        } catch (e) {
          return Promise.reject(error);
        }
      },
    );
  }

  private async handleUnauthorized(
    error: AxiosError,
    policy: 'retry' | 'login' = 'retry',
  ): Promise<AxiosResponse> {
    if ((error.response?.data as any)?.code === '2fa-invalid') {
      localStorage.setItem('next', this.router.url);
      this.router.navigateByUrl('2fa/verify');
      return Promise.reject(error);
    }
    await this.refreshAccessToken();
    if (this.accessToken && policy === 'retry') {
      const client = axios.create();
      this.setRequestInterceptor(client);
      this.setResponseInterceptor(client, 'login');
      return await client(error.config);
    } else {
      await this.router.navigateByUrl('login');
      return Promise.reject(error);
    }
  }

  private async handleForbidden(error: AxiosError<any>) {
    if (error.response?.data?.code === 'invalid-role') {
      this.snackbar.open('Nincs jogosultságod az oldal eléréséhez!', 'OK', {
        duration: 10_000,
      });
      this.clearTokens();
      this.router.navigateByUrl('login');
    }
    return Promise.reject(error);
  }

  private async refreshAccessToken(): Promise<void> {
    if (this.refreshingTokens.value) {
      await this.refreshingTokens.pipe(filter(v => !v), first()).toPromise();
      return;
    }

    this.refreshingTokens.next(true);

    try {
      this.accessToken = null;
      const uninterceptedAxiosInstance = axios.create();
      const res = await uninterceptedAxiosInstance.post(environment.baseUrl + `/api/oauth/refresh`, {
        refresh_token: this.refreshToken,
      });

      const {
        access_token,
        refresh_token,
        id_token,
      } = res.data;
      this.accessToken = access_token;
      this.refreshToken = refresh_token;
      this.idTokenHint = id_token;
      this.refreshingTokens.next(false);
    } catch (error) {
      this.refreshingTokens.next(false);
      this.dialog.closeAll();
      await this.logout();
    }
  }
}
